// --- Types ---
import type { LocalMicrophone } from "features/medical-scribe/microphone-storage";

export type MicrophoneCategory =
  | "clinical"
  | "built_in"
  | "bluetooth"
  | "usb"
  | "virtual"
  | "headset"
  | "unknown";

export type BrowserName = "chrome" | "edge" | "firefox" | "safari" | "unknown";

export type DeviceType = "physical" | "virtual" | "clinical";

export const MicrophoneVerificationStatus = {
  Verified: "verified",
  LikelyMatch: "likely_match",
  Unverified: "unverified"
} as const;

export type VerificationStatus =
  (typeof MicrophoneVerificationStatus)[keyof typeof MicrophoneVerificationStatus];

export type SilenceRisk = "low" | "medium" | "high";

export interface MicrophoneFingerprint {
  deviceId: string;
  groupId: string;
  label: string;
  normalizedLabel: string;
  category: MicrophoneCategory;
  userAgent: string;
  isVerified?: boolean;
}

export interface MicrophoneInput {
  device_id: string;
  group_id: string;
  label: string;
  normalized_label: string;
  category: string;
  user_agent: string;
}

export type { LocalMicrophone };

export interface MicrophoneAssessment {
  verification: VerificationStatus;
  score: number;
  matchedMicrophone: LocalMicrophone | null;
  deviceType: DeviceType;
  category: MicrophoneCategory;
  silenceRisk: SilenceRisk;
  canRecordImmediately: boolean;
}

export interface CategoryRule {
  category: MicrophoneCategory;
  patterns: RegExp[];
}

// --- Constants ---

export const SCORE_DEVICE_ID = 60;
export const SCORE_LABEL = 35;
export const SCORE_GROUP_ID = 10;
export const SCORE_CATEGORY = 5;
export const SCORE_BROWSER = 5;

export const THRESHOLD_VERIFIED = 60;
export const THRESHOLD_LIKELY = 40;

export const PSEUDO_DEVICE_IDS = ["default", "communications"];

export const PHYSICAL_CATEGORIES: MicrophoneCategory[] = [
  "built_in",
  "bluetooth",
  "usb",
  "headset",
  "unknown"
];

// --- Category Rules (ordered rule engine) ---

export const CATEGORY_RULES: CategoryRule[] = [
  // Rule 0 -- CLINICAL: audiology/clinical devices (must be checked first)
  {
    category: "clinical",
    patterns: [
      /\baurical\b/,
      /\botometrics\b/,
      /\botosuite\b/,
      /\binteracoustics\b/,
      /\bgrason[- ]?stadler\b/,
      /\bgsi[- ]?\d{2,3}\b/,
      /\bmaico\b/,
      /\bmedrx\b/,
      /\bavant\s*(aud|rem|ria)\b/,
      /\baudioscan\b/,
      /\bverifit\b/,
      /\baudiomet(er|ry)\b/,
      /\btympan(ometer|ometry)\b/,
      /\breal[- ]?ear\b/,
      /\bhi-?pro\s*(usb|2)\b/,
      /\bnoahlink\b/,
      /\bicube\b/,
      /\bnatus\s+(sensory|medical)\b/,
      /\b(phonak|signia|resound|oticon|widex|starkey|unitron)\s+(target|connexx|smart\s*fit|genie|compass|inspire|trufit)\b/,
      /\bcochlear\b/,
      /\bspeech[- ]?map\b/
    ]
  },

  // Rule 1 -- VIRTUAL: virtual devices and system loopbacks
  {
    category: "virtual",
    patterns: [
      /^monitor\s+of\b/,
      /\bvirtual\b/,
      /\bblackhole\b/,
      /\bsoundflower\b/,
      /\bloopback\s*audio\b/,
      /\bvb[- ]?(cable|audio)\b/,
      /\bvoicemod\b/,
      /\bkrisp\b/,
      /\bnvidia\s*broadcast\b/,
      /\bobs\s*(virtual|audio)\b/,
      /\bzoom\s*audio\s*device\b/,
      /\bscreencastify\b/,
      /\bstereaudio\b/
    ]
  },

  // Rule 2 -- BLUETOOTH: wireless audio devices
  {
    category: "bluetooth",
    patterns: [
      /\bbluetooth\b/,
      /\bairpods\b/,
      /\bgalaxy\s*buds\b/,
      /\bpixel\s*buds\b/,
      /\bbeats\s*(studio|flex|fit|solo|powerbeats)\b/,
      /\bjabra\s*(evolve2?\s*(75|85)|elite)\b/,
      /\bpoly\s*(voyager|focus)\b/,
      /\bplantronics\s*(voyager|explorer|backbeat)\b/,
      /\bbose\s*(qc|quietcomfort|700|nc)\b/,
      /\bsony\s*w[hf]-?\d/,
      /\bsennheiser\s*(momentum|ambeo)\b/,
      /\bjbl\s*(tune|live|club)\b/,
      /\b(wireless|bt)\s+(headset|headphone|earbuds?|earpiece)\b/,
      /\b(headset|headphone|earbuds?)\s+.*\bbluetooth\b/,
      /\bwh-\d{3,4}x?m\d\b/
    ]
  },

  // Rule 3 -- HEADSET: wired headsets (check before built-in)
  {
    category: "headset",
    patterns: [
      /\bheadset\s*(microphone|mic)\b/,
      /\bheadset\b.*\b(realtek|conexant|synaptics)\b/,
      /\blogitech\s*(usb\s*)?headset\b/,
      /\bplantronics\s*(blackwire|calisto)\b/,
      /\bpoly\s*(blackwire|calisto|sync)\b/,
      /\bjabra\s*(biz|evolve2?\s*(30|40|50)|speak)\b/,
      /\bcorsair\s*(void|hs\d+|virtuoso)\b/,
      /\bsteelseries\s*arctis\b/,
      /\bhyperx\s*cloud\b/,
      /\brazer\s*(kraken|barracuda|blackshark)\b/
    ]
  },

  // Rule 4 -- USB: dedicated USB mics, webcam mics, audio codecs, interfaces
  {
    category: "usb",
    patterns: [
      // Dedicated USB microphones
      /\b(blue\s*)?yeti\b/,
      /\bsnowball\b/,
      /\bsamson\s*(meteor|go\s*mic|q2u)\b/,
      /\brode\s*(nt-?usb|podmic|podcaster)\b/,
      /\bshure\s*(mv\d|sm\d+db|motiv)\b/,
      /\bat20\d{2}usb\b/,
      /\belgato\s*wave\b/,
      /\bhyperx\s*(quadcast|solocast|duocast)\b/,
      /\brazer\s*seiren\b/,
      /\bfifine\b/,
      /\bmaono\b/,
      /\btonor\b/,
      // Webcam microphones
      /\bstreamcam\b/,
      /\blogitech\s*(brio|c9[12]\d|c92[05]|c270|c310|c505)\b/,
      /\brazer\s*kiyo\b/,
      /\belgato\s*facecam\b/,
      /\bwebcam\b/,
      // USB audio chipsets and codecs
      /\bpcm29\d{2}[a-z]?\b/,
      /\bc-media\s*(usb|audio)\b/,
      /\busb\s*(pnp\s*)?audio\s*device\b/,
      /\busb\s*microphone\b/,
      // Audio interfaces
      /\bfocusrite\s*scarlett\b/,
      /\bpresonus\s*(audiobox|revelator)\b/,
      /\bbehringer\s*(u-?phoria|umc)\b/,
      /\buniversal\s*audio\s*volt\b/,
      /\baudient\b/,
      /\bmotu\s*m\d\b/
    ]
  },

  // Rule 5 -- BUILT-IN: laptop/phone internal microphones
  {
    category: "built_in",
    patterns: [
      // Explicit built-in keywords
      /\bbuilt[- ]?in\b/,
      /\binternal\s*(microphone|mic|audio)\b/,
      /\bintegrated\s*(microphone|mic|audio)\b/,
      // macOS / iOS device names
      /\bmacbook\s*(pro|air)?\s*microphone\b/,
      /\bimac\s*microphone\b/,
      /\biphone\s*microphone\b/,
      /\bipad\s*microphone\b/,
      // Windows label format (chipset in parentheses)
      /\bmicrophone\s*array\b/,
      /\bmicrophone\s*\(realtek\b/,
      /\bmicrophone\s*\(high\s*definition\b/,
      /\bmicrophone\s*\(conexant\b/,
      /\bmicrophone\s*\(synaptics\b/,
      /\bmicrophone\s*\(intel\b/,
      /\bmicrophone\s*\(cirrus\b/,
      // Linux label format (chipset name first, "Digital Microphone" at end)
      /\bdigital\s*microphone\b/,
      /\bhigh\s*definition\s*audio\s*controller\b.*\bmicrophone\b/,
      /\b(alder|tiger|cannon|comet|ice|raptor|meteor)\s*lake\b.*\bmicrophone\b/
    ]
  }
];

// --- Core Functions ---

export function detectBrowser(ua?: string): BrowserName {
  const userAgent =
    ua ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
  if (!userAgent) return "unknown";

  if (/\bEdg[e/]\b/i.test(userAgent)) return "edge";
  if (/\bChrom(e|ium)\b/i.test(userAgent)) return "chrome";
  if (/\bFirefox\b/i.test(userAgent)) return "firefox";
  if (/\bSafari\b/i.test(userAgent) && !/\bChrom/i.test(userAgent))
    return "safari";

  return "unknown";
}

export function normalizeLabel(label: string): string {
  let normalized = label.toLowerCase();

  // Strip browser "Default - " / "Default:" / "Communications - " prefixes
  normalized = normalized.replace(
    /^(default\s*[-:]\s*|communications\s*[-:]\s*)/,
    ""
  );

  // Remove trademark symbols
  normalized = normalized.replaceAll(/\(r\)|\(tm\)|®|™/g, "");

  // Strip apostrophes and smart quotes to avoid mismatch
  normalized = normalized.replaceAll(/['\u2018\u2019\u201C\u201D"]/g, "");

  // Normalize dashes: em dash, en dash, figure dash, horizontal bar -> space
  // Regular hyphens are preserved (meaningful in "built-in", "nt-usb", etc.)
  normalized = normalized.replaceAll(/[\u2012\u2013\u2014\u2015]/g, " ");

  // Collapse whitespace and trim
  normalized = normalized.replaceAll(/\s+/g, " ").trim();

  return normalized;
}

export function classifyMicrophone(
  normalizedLabel: string
): MicrophoneCategory {
  if (!normalizedLabel) return "unknown";

  for (const rule of CATEGORY_RULES) {
    for (const pattern of rule.patterns) {
      if (pattern.test(normalizedLabel)) {
        return rule.category;
      }
    }
  }

  return "unknown";
}

export function createMicrophoneFingerprint(
  device: MediaDeviceInfo
): MicrophoneFingerprint {
  const normalizedLabel = normalizeLabel(device.label);

  return {
    deviceId: device.deviceId,
    groupId: device.groupId,
    label: device.label,
    normalizedLabel,
    category: classifyMicrophone(normalizedLabel),
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : ""
  };
}

export async function getAvailableMicrophones(): Promise<
  MicrophoneFingerprint[]
> {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.enumerateDevices
  ) {
    return [];
  }

  const devices = await navigator.mediaDevices.enumerateDevices();
  const audioInputs = devices.filter((d) => d.kind === "audioinput");

  // Filter out pseudo-devices ("default", "communications") in Chromium browsers.
  // These are OS-level proxies that can point to different hardware at any time.
  const realDevices = audioInputs.filter(
    (d) => !PSEUDO_DEVICE_IDS.includes(d.deviceId.toLowerCase())
  );

  // If filtering removed everything (non-Chromium browsers), use the original list
  const source = realDevices.length > 0 ? realDevices : audioInputs;

  return source.map(createMicrophoneFingerprint);
}

/**
 * Prompts for microphone permission via getUserMedia, reads the active input's deviceId
 * from track settings, then stops all tracks so recording can acquire a fresh stream later.
 */
export async function requestMicrophonePermissionForEnumeration(
  savedDeviceId: string
): Promise<{ activeDeviceId: string | undefined }> {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.getUserMedia
  ) {
    throw new Error("Microphone access is not available in this environment.");
  }

  const trimmed = savedDeviceId?.trim() ?? "";
  const useDefaultAudio =
    !trimmed || PSEUDO_DEVICE_IDS.includes(trimmed.toLowerCase());

  let stream: MediaStream;
  if (useDefaultAudio) {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } else {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: trimmed } }
      });
    } catch (error) {
      // Stale deviceId (new browser profile, private mode, OS change) is not always
      // OverconstrainedError across browsers — fall back so the user still gets a prompt.
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        throw error;
      }
    }
  }

  const track = stream.getAudioTracks()[0];
  const activeDeviceId = track?.getSettings?.()?.deviceId;
  stream.getTracks().forEach((t) => t.stop());

  return { activeDeviceId: activeDeviceId || undefined };
}

// --- Scoring & Matching ---

export function computeMatchScore(
  fingerprint: MicrophoneFingerprint,
  verified: LocalMicrophone
): number {
  let score = 0;

  if (
    fingerprint.deviceId &&
    verified.device_id &&
    fingerprint.deviceId === verified.device_id
  ) {
    score += SCORE_DEVICE_ID;
  }

  if (
    fingerprint.normalizedLabel &&
    verified.normalized_label &&
    fingerprint.normalizedLabel === verified.normalized_label
  ) {
    score += SCORE_LABEL;
  }

  if (
    fingerprint.groupId &&
    verified.group_id &&
    fingerprint.groupId === verified.group_id
  ) {
    score += SCORE_GROUP_ID;
  }

  if (
    fingerprint.category !== "unknown" &&
    verified.category !== "unknown" &&
    fingerprint.category === verified.category
  ) {
    score += SCORE_CATEGORY;
  }

  if (
    detectBrowser(fingerprint.userAgent) === detectBrowser(verified.user_agent)
  ) {
    score += SCORE_BROWSER;
  }

  return score;
}

function isPseudoDeviceId(deviceId: string): boolean {
  return !deviceId || PSEUDO_DEVICE_IDS.includes(deviceId.toLowerCase());
}

function getDeviceType(category: MicrophoneCategory): DeviceType {
  if (category === "virtual") return "virtual";
  if (category === "clinical") return "clinical";
  return "physical";
}

function getSilenceRisk(deviceType: DeviceType): SilenceRisk {
  if (deviceType === "virtual") return "high";
  if (deviceType === "clinical") return "medium";
  return "low";
}

export function assessMicrophone(
  fingerprint: MicrophoneFingerprint,
  microphones: LocalMicrophone[]
): MicrophoneAssessment {
  const deviceType = getDeviceType(fingerprint.category);
  const silenceRisk = getSilenceRisk(deviceType);

  let bestScore = 0;
  let matchedMicrophone: LocalMicrophone | null = null;
  let overrideFired = false;

  for (const microphone of microphones) {
    // Override rule: exact deviceId + browser match with non-pseudo deviceId
    if (
      !isPseudoDeviceId(fingerprint.deviceId) &&
      fingerprint.deviceId === microphone.device_id &&
      detectBrowser(fingerprint.userAgent) ===
        detectBrowser(microphone.user_agent)
    ) {
      overrideFired = true;
      matchedMicrophone = microphone;
      bestScore = Math.max(
        bestScore,
        computeMatchScore(fingerprint, microphone)
      );
      break;
    }

    const score = computeMatchScore(fingerprint, microphone);
    if (score > bestScore) {
      bestScore = score;
      matchedMicrophone = microphone;
    }
  }

  let verification: VerificationStatus;
  if (overrideFired || bestScore >= THRESHOLD_VERIFIED) {
    verification = MicrophoneVerificationStatus.Verified;
  } else if (bestScore >= THRESHOLD_LIKELY) {
    verification = MicrophoneVerificationStatus.LikelyMatch;
  } else {
    verification = MicrophoneVerificationStatus.Unverified;
    if (bestScore === 0) {
      matchedMicrophone = null;
    }
  }

  const canRecordImmediately =
    verification === MicrophoneVerificationStatus.Verified ||
    (verification === MicrophoneVerificationStatus.LikelyMatch &&
      deviceType === "physical") ||
    (verification === MicrophoneVerificationStatus.LikelyMatch &&
      silenceRisk === "low");

  return {
    verification,
    score: bestScore,
    matchedMicrophone,
    deviceType,
    category: fingerprint.category,
    silenceRisk,
    canRecordImmediately
  };
}

// --- Convenience Helpers ---

export function fingerprintToInput(fp: MicrophoneFingerprint): MicrophoneInput {
  return {
    device_id: fp.deviceId,
    group_id: fp.groupId,
    label: fp.label,
    normalized_label: fp.normalizedLabel,
    category: fp.category,
    user_agent: fp.userAgent
  };
}

/**
 * Turns a stored `deviceSettings.microphone` value into a value safe to use as an exact
 * getUserMedia constraint, or `undefined` meaning "send no constraint".
 *
 * "default" / "communications" are OS-level proxies rather than real device ids — "default"
 * is also what the slice stores when the user never picked a specific mic — and neither
 * resolves as a deviceId on Firefox, so constraining to them fails there.
 */
export function normalizeMicrophoneDeviceId(
  storedDeviceId: string | undefined
): string | undefined {
  const trimmed = storedDeviceId?.trim() ?? "";
  if (!trimmed || PSEUDO_DEVICE_IDS.includes(trimmed.toLowerCase())) {
    return undefined;
  }
  return trimmed;
}

/**
 * Re-enumerates inputs and builds the WS microphone payload for a single device id.
 * Returns undefined when there is no id, no matching input, or enumeration fails
 * (enumerateDevices throws when permissions are revoked mid-session).
 */
export async function resolveMicrophoneInputForDeviceId(
  deviceId: string | undefined
): Promise<MicrophoneInput | undefined> {
  if (!deviceId) {
    return undefined;
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const device = devices.find(
      (d) => d.kind === "audioinput" && d.deviceId === deviceId
    );
    return device
      ? fingerprintToInput(createMicrophoneFingerprint(device))
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Payload for a device we know captured the audio but cannot describe — the track reported
 * a deviceId that enumerateDevices no longer resolves (labels stripped, device rotated).
 * Records the real device id with an empty label rather than borrowing the label of the
 * device the user *selected*, which may not be the one that opened.
 */
export function buildMinimalMicrophoneInput(deviceId: string): MicrophoneInput {
  return fingerprintToInput(
    createMicrophoneFingerprint({
      deviceId,
      groupId: "",
      label: "",
      kind: "audioinput"
    } as MediaDeviceInfo)
  );
}

/**
 * Which microphone the WS `start_recording` payload should describe.
 *
 * The payload must name the device the audio was actually captured from — a fingerprint that
 * names hardware which never opened makes the verified-microphone record worse than useless.
 * The opened track's own settings are the authority on that, when they say anything at all.
 *
 * Both ids are normalized here rather than at the call site: `getSettings().deviceId` reports
 * the pseudo id "default" whenever the stream opened without a deviceId constraint, and a
 * pseudo id carries no device identity. Left raw it would outrank a fingerprint that had
 * already resolved the real hardware, which is the ENG-1652 regression. Owning the
 * normalization makes that unrepeatable instead of a rule callers have to remember.
 */
export async function resolveStartRecordingMicrophone({
  knownFingerprint,
  capturedDeviceId: rawCapturedDeviceId,
  selectedDeviceId: rawSelectedDeviceId
}: {
  knownFingerprint: MicrophoneInput | undefined;
  capturedDeviceId: string | undefined;
  selectedDeviceId: string | undefined;
}): Promise<MicrophoneInput | undefined> {
  const capturedDeviceId = normalizeMicrophoneDeviceId(rawCapturedDeviceId);
  const selectedDeviceId = normalizeMicrophoneDeviceId(rawSelectedDeviceId);

  // Richest source (it resolved the active device, including the Firefox private-mode
  // activeDeviceId fallback), but only trusted while it agrees with the device that opened.
  if (
    knownFingerprint &&
    (!capturedDeviceId || knownFingerprint.device_id === capturedDeviceId)
  ) {
    return knownFingerprint;
  }

  if (capturedDeviceId) {
    return (
      (await resolveMicrophoneInputForDeviceId(capturedDeviceId)) ??
      buildMinimalMicrophoneInput(capturedDeviceId)
    );
  }

  // The track reported no usable id (Firefox private mode) and we have nothing threaded in:
  // describe the selection instead, which is what we asked the browser to open.
  return resolveMicrophoneInputForDeviceId(selectedDeviceId);
}

export function isVirtualMicrophone(
  fingerprint: MicrophoneFingerprint
): boolean {
  return fingerprint.category === "virtual";
}

export function isClinicalDevice(fingerprint: MicrophoneFingerprint): boolean {
  return fingerprint.category === "clinical";
}

export function isMicrophoneVerified(
  fingerprint: MicrophoneFingerprint,
  verifiedMics: LocalMicrophone[]
): boolean {
  return (
    assessMicrophone(fingerprint, verifiedMics).verification ===
    MicrophoneVerificationStatus.Verified
  );
}
